import {
  MARGIN_X,
  RULE_OFFSET,
  RULE_SPACING,
  RULE_THICKNESS,
  lineY,
} from "../notebookGrid";

// Ruled-paper sheet rendered in the tldraw OnTheCanvas slot so it sits in page
// space and pans/zooms with the ink, over the fixed desk texture behind it.
// Single source of truth for the paper size (imported by TldrawCanvasAgent);
// the ruling geometry comes from notebookGrid so paper and snapping can't drift.
//
// tldraw's OnTheCanvas slot can't be truly infinite (it's a single painted div
// with a CSS gradient), so the sheet is sized far larger than any lesson could
// fill — effectively unlimited room to the right and below. Kept under the
// ~16384px GPU-texture limit per axis so the browser never fails to raster it.
export const PAGE_WIDTH = 16000;
export const PAGE_HEIGHT = 16000;

const RULE_COLOR = "rgba(72,104,163,0.13)";
const MARGIN_COLOR = "rgba(214,74,74,0.32)";

// Blue horizontal rules (repeating every RULE_SPACING, RULE_THICKNESS thick,
// offset by RULE_OFFSET) and the red vertical left margin at MARGIN_X, all
// derived from the shared notebookGrid constants.
const BACKGROUND_IMAGE =
  `linear-gradient(to right, transparent 0, transparent ${MARGIN_X - RULE_THICKNESS}px, ${MARGIN_COLOR} ${MARGIN_X - RULE_THICKNESS}px, ${MARGIN_COLOR} ${MARGIN_X}px, transparent ${MARGIN_X}px), ` +
  `repeating-linear-gradient(to bottom, transparent 0, transparent ${RULE_SPACING - RULE_THICKNESS}px, ${RULE_COLOR} ${RULE_SPACING - RULE_THICKNESS}px, ${RULE_COLOR} ${RULE_SPACING}px)`;

const LINE_NUMBER_COLOR = "rgba(120,96,44,0.34)";
const LINE_NUMBER_FONT_PX = 13;
/** Gap between a line number's right edge and the red margin. */
const LINE_NUMBER_INSET = 8;

/**
 * Printed line numbers down the left gutter, like a numbered legal pad.
 *
 * Doubles as a SHARED COORDINATE FRAME with the agent. The scene digest locates
 * the student's handwriting by rule line ("line 12, left of column main"), which
 * only helps if "line 12" is something the agent can find in the screenshot —
 * printing the numbers makes that a lookup instead of an inference, at no
 * per-turn cost and with nothing to keep in sync but `lineY`.
 *
 * LEFT GUTTER ONLY. Flow columns build rightward across the sheet, and a student
 * panned to column 2 has these numbers off-screen; there they fall back on the
 * digest's positional description and the screenshot's ink labels. Numbering each
 * column's own gutter would mean painting from the live column width, and this
 * sheet deliberately knows nothing about layout (see notebookGrid.ts).
 *
 * Static, so it is built once at module load rather than per render.
 */
const LINE_NUMBERS = (() => {
  const nodes = [];
  for (let n = 1; lineY(n) < PAGE_HEIGHT; n++) {
    nodes.push(
      <div
        key={n}
        style={{
          position: "absolute",
          // Right-aligned against the red margin, so the digits line up in a
          // column however many of them there are.
          left: 0,
          width: MARGIN_X - LINE_NUMBER_INSET,
          textAlign: "right",
          // Baseline on the rule, the way writing sits on it.
          top: lineY(n) - LINE_NUMBER_FONT_PX,
          lineHeight: `${LINE_NUMBER_FONT_PX}px`,
          fontSize: LINE_NUMBER_FONT_PX,
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontVariantNumeric: "tabular-nums",
          color: LINE_NUMBER_COLOR,
          userSelect: "none",
        }}
      >
        {n}
      </div>,
    );
  }
  return nodes;
})();

export function CanvasPaperBackground() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        pointerEvents: "none",
        backgroundColor: "#fbf6e6",
        backgroundImage: BACKGROUND_IMAGE,
        backgroundRepeat: "no-repeat, repeat",
        backgroundSize: `100% 100%, 100% ${RULE_SPACING}px`,
        backgroundPosition: `0 0, 0 ${RULE_OFFSET}px`,
        boxShadow: "0 12px 44px rgba(120,96,44,0.22)",
      }}
    >
      {LINE_NUMBERS}
    </div>
  );
}
